/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Slide,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  GetApp,
  Close,
  PhoneIphone,
  Bolt,
  SignalCellularAlt,
  NotificationsActive,
  ArrowBack as ArrowBackIcon,
  MarkEmailRead as MarkEmailReadIcon,
  WorkOutline as WorkOutlineIcon,
  Groups as GroupsIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';
import { signInUser, signInWithGoogle, resetPassword, createUser } from '../firebase';
import { isLoggedIn } from '../session';
import logo from "../imgs/logo_combined.png";

export const SKILLS_OPTIONS = [
  "Diseño Web",
  "Diseño UX/UI",
  "Diseño Grafico",
  "Programación",
  "Desarrollo de Apps",
  "Community Manager",
  "Marketing Digital",
  "Diseño Multimedia",
  "Edición de Videos",
  "Edición de Imágenes",
  "Redacción de Contenidos",
  "Traducción",
];

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const LogoHeader = () => (
  <Box
    component="img"
    src={logo}
    alt="Skach"
    sx={{
      width: 260,
      height: 'auto',
      display: 'block',
      mx: 'auto',
      mb: 3,
    }}
  />
);

const PRESENTATION_FEATURES = [
  {
    icon: WorkOutlineIcon,
    title: 'Publicá tu proyecto',
    description: 'Recibí propuestas de freelancers calificados en minutos.',
  },
  {
    icon: GroupsIcon,
    title: 'Encontrá oportunidades',
    description: 'Postulate a proyectos que se ajusten a tus habilidades.',
  },
  {
    icon: VerifiedUserIcon,
    title: 'Gestión segura',
    description: 'Chat, propuestas y pagos, todo dentro de la plataforma.',
  },
];

const PresentationPanel = () => (
  <Box
    sx={{
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      justifyContent: 'center',
      width: '46%',
      px: 7,
      py: 8,
      position: 'relative',
      overflow: 'hidden',
      background:
        'radial-gradient(circle at 15% 15%, rgba(34,217,238,0.22), transparent 45%), ' +
        'radial-gradient(circle at 85% 85%, rgba(58,92,255,0.28), transparent 50%), #0A0B12',
      color: '#fff',
    }}
  >
    <Typography variant="h3" fontWeight={700} gutterBottom sx={{ maxWidth: 420, lineHeight: 1.2 }}>
      Conectá talento con proyectos
    </Typography>
    <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 400, mb: 6, maxWidth: 420 }}>
      ¡Creá un proyecto o buscá uno! La plataforma donde clientes y freelancers trabajan juntos.
    </Typography>

    <Stack spacing={3}>
      {PRESENTATION_FEATURES.map(({ icon: Icon, title, description }) => (
        <Stack direction="row" spacing={2} alignItems="flex-start" key={title}>
          <Icon sx={{ color: 'primary.light', fontSize: 28, mt: 0.5 }} />
          <Box>
            <Typography fontWeight={600}>{title}</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              {description}
            </Typography>
          </Box>
        </Stack>
      ))}
    </Stack>
  </Box>
);

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const view =
    location.pathname === '/register'
      ? 'register'
      : location.pathname === '/forgotPassword'
      ? 'forgot'
      : 'login';

  // ---- Inicio de sesión ----
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // ---- Olvidé mi contraseña ----
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // ---- Registro ----
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPoint, setRegPoint] = useState('');
  const [regSkills, setRegSkills] = useState([]);
  const [regBio, setRegBio] = useState('');
  const [regWebsite, setRegWebsite] = useState('');
  const [regLinkedin, setRegLinkedin] = useState('');
  const [regGithub, setRegGithub] = useState('');
  const [regDisplayName, setRegDisplayName] = useState('');
  const [regUserType, setRegUserType] = useState('cliente');
  const [regError, setRegError] = useState('');
  const [regShowPassword, setRegShowPassword] = useState(false);

  // Detectar si es dispositivo móvil y manejar evento de instalación de PWA
  useEffect(() => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);

      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      const dismissedTime = localStorage.getItem('installPromptDismissed');
      const shouldShowAgain = !dismissedTime || (Date.now() - parseInt(dismissedTime)) > 86400000; // 24 horas

      if (isMobile && !isStandalone && shouldShowAgain) {
        setTimeout(() => {
          setShowInstallPrompt(true);
        }, 2000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    await deferredPrompt.userChoice;

    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismissInstall = () => {
    setShowInstallPrompt(false);
    localStorage.setItem('installPromptDismissed', Date.now().toString());
  };

  const handleChange = (prop) => (event) => {
    setFormData({ ...formData, [prop]: event.target.value });
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      setError('Por favor ingrese su usuario y contraseña.');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Por favor ingrese un email válido.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const loginResponse = await signInUser(formData.email, formData.password);
      isLoggedIn(loginResponse.user);
      navigate('/home');
    } catch (error) {
      console.error(error.message);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      const result = await signInWithGoogle();
      isLoggedIn(result.user);
      navigate(result.isNewUser ? '/edit' : '/home');
    } catch (error) {
      console.error(error.message);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotSubmit = async (event) => {
    event.preventDefault();
    setForgotError('');

    if (!forgotEmail) {
      setForgotError('Por favor ingresá tu dirección de correo electrónico.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotEmail)) {
      setForgotError('Por favor ingresá un email válido.');
      return;
    }

    setForgotLoading(true);
    try {
      await resetPassword(forgotEmail);
      setForgotSent(true);
    } catch (err) {
      console.error(err.message);
      setForgotError('No pudimos enviar el email. Verificá la dirección e intentá de nuevo.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleRegSkillsChange = (event) => {
    const { value } = event.target;
    setRegSkills(typeof value === 'string' ? value.split(',') : value);
  };

  const handleRegChangeUserType = (event) => {
    setRegUserType(event.target.value);
    if (event.target.value === 'cliente') {
      setRegSkills([]);
    }
  };

  const handleRegisterSubmit = async () => {
    try {
      if (regPassword !== regConfirmPassword) {
        throw new Error('Las contraseñas no coinciden.');
      }

      await createUser(
        regEmail,
        regPassword,
        regDisplayName,
        regPhone,
        regPoint,
        regUserType === 'freelancer' ? regSkills : [],
        regUserType,
        regBio,
        regUserType === 'freelancer' ? { website: regWebsite, linkedin: regLinkedin, github: regGithub } : {}
      );

      setRegEmail('');
      setRegPassword('');
      setRegConfirmPassword('');
      setRegPhone('');
      setRegPoint('');
      setRegSkills([]);
      setRegBio('');
      setRegWebsite('');
      setRegLinkedin('');
      setRegGithub('');
      setRegUserType('cliente');
      setRegError('');
      navigate('/home');
    } catch (error) {
      setRegError(error.message);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <PresentationPanel />

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            overflowY: 'auto',
            py: 4,
          }}
        >
      {view === 'login' && (
        <Box sx={{ maxWidth: 380, mx: 'auto', px: 3, width: '100%' }}>
          <LogoHeader />

          <Typography variant="h5" component="h1" textAlign="center" fontWeight="500" gutterBottom>
            Inicio de sesión
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <FormControl fullWidth margin="normal">
              <InputLabel htmlFor="email">Email</InputLabel>
              <OutlinedInput
                id="email"
                type="email"
                label="Email"
                value={formData.email}
                onChange={handleChange('email')}
                placeholder="usuario@gmail.com"
                disabled={isLoading}
              />
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel htmlFor="password">Contraseña</InputLabel>
              <OutlinedInput
                id="password"
                type={showPassword ? 'text' : 'password'}
                label="Contraseña"
                value={formData.password}
                onChange={handleChange('password')}
                placeholder="••••••••••••"
                disabled={isLoading}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" disabled={isLoading}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{ mt: 3, height: 42, textTransform: 'none', fontSize: '1rem' }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Iniciar sesión'}
            </Button>

            <Stack spacing={1} alignItems="center" sx={{ mt: 3 }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/forgotPassword')}
                disabled={isLoading}
                sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Olvidé mi contraseña
              </Link>

              <Typography variant="body2" color="text.secondary">
                ¿Aún no tienes cuenta?{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/register')}
                  sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  Registrarse
                </Link>
              </Typography>
            </Stack>
          </Box>
        </Box>
      )}

      {view === 'forgot' && (
        <Box sx={{ maxWidth: 380, mx: 'auto', px: 3, width: '100%' }}>
          <LogoHeader />

          {forgotSent ? (
            <Box sx={{ textAlign: 'center' }}>
              <MarkEmailReadIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" component="h1" fontWeight="500" gutterBottom>
                Revisá tu email
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Te enviamos un link a <strong>{forgotEmail}</strong> para restablecer tu contraseña.
                Si no lo ves, revisá la carpeta de spam.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{ height: 42, textTransform: 'none', fontSize: '1rem' }}
              >
                Volver a iniciar sesión
              </Button>
            </Box>
          ) : (
            <>
              <Typography variant="h5" component="h1" textAlign="center" fontWeight="500" gutterBottom>
                Recuperar contraseña
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
                Ingresá el email de tu cuenta y te mandamos un link para crear una contraseña nueva.
              </Typography>

              {forgotError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
                  {forgotError}
                </Alert>
              )}

              <Box component="form" onSubmit={handleForgotSubmit} noValidate>
                <FormControl fullWidth margin="normal">
                  <InputLabel htmlFor="forgot-email">Email</InputLabel>
                  <OutlinedInput
                    id="forgot-email"
                    type="email"
                    label="Email"
                    value={forgotEmail}
                    onChange={(e) => {
                      setForgotEmail(e.target.value);
                      if (forgotError) setForgotError('');
                    }}
                    placeholder="usuario@gmail.com"
                    disabled={forgotLoading}
                    autoFocus
                  />
                </FormControl>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={forgotLoading}
                  sx={{ mt: 3, height: 42, textTransform: 'none', fontSize: '1rem' }}
                >
                  {forgotLoading ? <CircularProgress size={24} color="inherit" /> : 'Enviar link de recuperación'}
                </Button>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/login')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 16 }} />
                  Volver a iniciar sesión
                </Link>
              </Box>
            </>
          )}
        </Box>
      )}

      {view === 'register' && (
        <Box sx={{ maxWidth: 420, mx: 'auto', px: 3, width: '100%' }}>
          <LogoHeader />
          <Typography variant="h5" component="h1" fontWeight="500" gutterBottom textAlign="center">
            Crear cuenta
          </Typography>
          {regError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
              {regError}
            </Alert>
          )}
          <Box component="form">
            <TextField
              label="Correo Electrónico"
              fullWidth
              margin="normal"
              value={regEmail}
              onChange={(e) => setRegEmail(e.target.value)}
            />
            <TextField
              label="Contraseña"
              variant="outlined"
              type={regShowPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={regPassword}
              onChange={(e) => setRegPassword(e.target.value)}
              fullWidth
              sx={{ mt: 3 }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setRegShowPassword(!regShowPassword)} edge="end">
                    {regShowPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
            <TextField
              label="Repetir Contraseña"
              fullWidth
              type={regShowPassword ? 'text' : 'password'}
              margin="normal"
              value={regConfirmPassword}
              onChange={(e) => setRegConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setRegShowPassword(!regShowPassword)} edge="end">
                    {regShowPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
            <Typography>Seleccione el tipo de usuario:</Typography>
            <Select
              sx={{ mt: 1.5, marginBottom: 1 }}
              value={regUserType}
              onChange={handleRegChangeUserType}
              fullWidth
              label="Tipo de Usuario"
            >
              <MenuItem value="cliente">Cliente</MenuItem>
              <MenuItem value="freelancer">Freelancer</MenuItem>
            </Select>
            <TextField
              label={regUserType === 'freelancer' ? 'Nombre de Freelancer' : 'Nombre de Cliente'}
              fullWidth
              margin="normal"
              value={regDisplayName}
              onChange={(e) => setRegDisplayName(e.target.value)}
            />
            <TextField
              label="Número de Teléfono"
              fullWidth
              margin="normal"
              value={regPhone}
              onChange={(e) => setRegPhone(e.target.value)}
            />
            <TextField
              label="Punto (Localidad)"
              fullWidth
              margin="normal"
              value={regPoint}
              onChange={(e) => setRegPoint(e.target.value)}
            />
            <TextField
              label={regUserType === 'freelancer' ? 'Bio (contá tu experiencia)' : 'Bio (contá sobre tu empresa/proyecto)'}
              fullWidth
              multiline
              rows={3}
              margin="normal"
              value={regBio}
              onChange={(e) => setRegBio(e.target.value)}
            />
            {regUserType === 'freelancer' && (
              <>
                <FormControl fullWidth sx={{ mt: 1.5, mb: 1 }}>
                  <InputLabel id="skills-select-label">Habilidades</InputLabel>
                  <Select
                    labelId="skills-select-label"
                    label="Habilidades"
                    multiple
                    value={regSkills}
                    onChange={handleRegSkillsChange}
                    input={<OutlinedInput label="Habilidades" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {SKILLS_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        <Checkbox checked={regSkills.indexOf(option) > -1} />
                        <ListItemText primary={option} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Sitio web / portfolio (opcional)"
                  fullWidth
                  margin="normal"
                  value={regWebsite}
                  onChange={(e) => setRegWebsite(e.target.value)}
                />
                <TextField
                  label="LinkedIn (opcional)"
                  fullWidth
                  margin="normal"
                  value={regLinkedin}
                  onChange={(e) => setRegLinkedin(e.target.value)}
                />
                <TextField
                  label="GitHub (opcional)"
                  fullWidth
                  margin="normal"
                  value={regGithub}
                  onChange={(e) => setRegGithub(e.target.value)}
                />
              </>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleRegisterSubmit}
              sx={{ mt: 3, height: 42, textTransform: 'none', fontSize: '1rem' }}
              fullWidth
            >
              Registrarse
            </Button>
          </Box>
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              ¿Ya tenés cuenta?{' '}
              <Link
                component="button"
                type="button"
                onClick={() => navigate('/login')}
                sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Iniciar sesión
              </Link>
            </Typography>
          </Box>
        </Box>
      )}
        </Box>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        textAlign="center"
        sx={{ py: 2 }}
      >
        © 2026 Skach - Dapur Tecnología. Todos los derechos reservados.
      </Typography>
      </Box>

      {/* PWA Install Prompt Dialog */}
      <Dialog
        open={showInstallPrompt}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleDismissInstall}
        PaperProps={{
          sx: {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            m: 0,
            borderRadius: '24px 24px 0 0',
            maxWidth: '100%',
            width: '100%',
            maxHeight: '80vh',
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
          },
        }}
      >
        <Box sx={{ position: 'relative', p: 3 }}>
          <IconButton
            onClick={handleDismissInstall}
            sx={{ position: 'absolute', top: 12, right: 12, color: 'text.secondary' }}
          >
            <Close />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, mt: 1 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 2,
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <PhoneIphone sx={{ fontSize: 36, color: 'white' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                ¡Instala nuestra app!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Accede más rápido y disfruta de una mejor experiencia
              </Typography>
            </Box>
          </Box>

          <Stack spacing={1.5} sx={{ mb: 3 }}>
            <Chip
              icon={<Bolt />}
              label="Carga instantánea y más rápida"
              variant="outlined"
              sx={{ justifyContent: 'flex-start', py: 2.5, px: 1.5, '& .MuiChip-label': { px: 1 } }}
            />
            <Chip
              icon={<SignalCellularAlt />}
              label="Funciona sin conexión"
              variant="outlined"
              sx={{ justifyContent: 'flex-start', py: 2.5, px: 1.5, '& .MuiChip-label': { px: 1 } }}
            />
            <Chip
              icon={<NotificationsActive />}
              label="Recibe notificaciones importantes"
              variant="outlined"
              sx={{ justifyContent: 'flex-start', py: 2.5, px: 1.5, '& .MuiChip-label': { px: 1 } }}
            />
          </Stack>

          <Stack spacing={1.5}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<GetApp />}
              onClick={handleInstallClick}
              sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem', fontWeight: 600 }}
            >
              Instalar Aplicación
            </Button>
            <Button
              fullWidth
              variant="text"
              size="large"
              onClick={handleDismissInstall}
              sx={{ textTransform: 'none', fontSize: '0.95rem', color: 'text.secondary' }}
            >
              Ahora no
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
            Podrás desinstalarla cuando quieras desde la configuración
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

export default Login;
